import { useIntl } from 'react-intl';
import { ResponsiveLine } from '@nivo/line';
import { Typography } from 'antd';
import { currencyFormatter, metricPrefixFormatter } from '@/utils/number';
import { ContentWrapper, EllipsisButton } from '@/components/_common';
import React, { useEffect } from 'react';

import { useChartTheme, useConvertChartData, useDisclosure } from '@/hooks';

import moment from 'moment';
import SalesInsuranceComparisonModal from './SalesInsuranceComparisonModal';
import { useChartComparisonInsurers } from '@/core/domain/workorder/ManagementCharts/ManagementChart.hooks';
import { DateTimeFormat } from '@/utils/moment';
import {
  ComparasionInsurersFormValues,
  ComparasionInsurersParams,
} from '@/core/domain/workorder/ManagementCharts/ManagementChart.types';

const { Title, Paragraph } = Typography;

const startDate = moment().subtract(5, 'months').startOf('day');
const endDate = moment().endOf('day');

function SalesInsuranceComparison() {
  const intl = useIntl();
  const chartTheme = useChartTheme();
  const [modalVisible, modalVisibleAction] = useDisclosure();

  const chartComparisonInsurers = useChartComparisonInsurers();

  const chartData = useConvertChartData(
    chartComparisonInsurers.data?.chartData,
  );

  useEffect(() => {
    chartComparisonInsurers.mutateAsync({
      startDate: startDate.format(DateTimeFormat),
      endDate: endDate.format(DateTimeFormat),
      managementChartType: 0,
    });
  }, []);

  const handleOk = async (values: ComparasionInsurersFormValues) => {
    try {
      const obj: ComparasionInsurersParams = {
        ...values,
        startDate: values.startDate.format(DateTimeFormat),
        endDate: values.endDate.format(DateTimeFormat),
      };

      await chartComparisonInsurers.mutateAsync(obj);

      modalVisibleAction.close();
    } catch {}
  };

  return (
    <>
      <ContentWrapper
        style={{ height: '430px', padding: '8px' }}
        extra={<EllipsisButton onClick={modalVisibleAction.open} />}
      >
        <Title level={5} style={{ textAlign: 'center', marginBottom: '4px' }}>
          {`Comparativo por Seguradoras`}
        </Title>

        <Paragraph style={{ textAlign: 'center', margin: '4px 0 4px 0' }}>
          {`Período: ${moment(
            chartComparisonInsurers.variables?.startDate,
          ).format('DD/MM/YYYY')} a ${moment(
            chartComparisonInsurers.variables?.endDate,
          ).format('DD/MM/YYYY')}`}
        </Paragraph>

        <ResponsiveLine
          data={chartData ? chartData : []}
          enableSlices="x"
          margin={{ top: 15, right: 25, bottom: 90, left: 70 }}
          xScale={{ type: 'point' }}
          yScale={{
            type: 'linear',
            min: 0,
            max: 'auto',
            reverse: false,
          }}
          yFormat={(value) => `${currencyFormatter(value)}`}
          curve="monotoneX"
          axisTop={null}
          axisRight={null}
          axisBottom={{
            tickSize: 7,
            tickPadding: 10,
            tickRotation: 0,
            legendOffset: 36,
            legendPosition: 'middle',
          }}
          axisLeft={{
            tickSize: 5,
            tickPadding: 5,
            tickRotation: 0,
            tickValues: 5,
            legend: intl.formatMessage({
              id: 'dashboard.averageTicketReport.averageTicketPartsAndServices',
            }),
            format: (value) => `${metricPrefixFormatter(+value)}`,
            legendOffset: -50,
            legendPosition: 'middle',
          }}
          enableGridX={false}
          pointColor={{ theme: 'background' }}
          pointBorderWidth={2}
          pointBorderColor={{ from: 'serieColor' }}
          pointLabelYOffset={-12}
          useMesh
          theme={chartTheme}
        />
      </ContentWrapper>

      <SalesInsuranceComparisonModal
        open={modalVisible}
        handleOk={handleOk}
        onCancel={modalVisibleAction.close}
      />
    </>
  );
}

export default SalesInsuranceComparison;
